import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType, AuthState, LoginRequest, SignupRequest } from '../types/auth.types';
import authService from '../services/auth.service';

// Create Auth Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Initial auth state
const initialAuthState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  refreshToken: null,
  loading: true,
  error: null,
};

// Auth provider props
interface AuthProviderProps {
  children: ReactNode;
}

// Auth provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>(initialAuthState);

  // Check for existing token on mount
  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const token = localStorage.getItem('token');
        const refreshToken = localStorage.getItem('refreshToken');

        if (token && authService.isLoggedIn()) {
          const user = authService.getCurrentUser();

          setAuth({
            isAuthenticated: true,
            user,
            token,
            refreshToken,
            loading: false,
            error: null,
          });
        } else {
          // Clear any invalid tokens
          authService.logout();
          setAuth({
            ...initialAuthState,
            loading: false,
          });
        }
      } catch (error) {
        console.error('Auth status check error:', error);
        // Clear any invalid tokens
        authService.logout();
        setAuth({
          ...initialAuthState,
          loading: false,
        });
      }
    };

    checkAuthStatus();
  }, []);

  // Login function
  const login = async (credentials: LoginRequest) => {
    try {
      setAuth(prev => ({ ...prev, loading: true, error: null }));

      const response = await authService.login(credentials);

      setAuth({
        isAuthenticated: true,
        user: {
          id: response.id,
          username: response.username,
          email: response.email,
          roles: response.roles,
        },
        token: response.token,
        refreshToken: response.refreshToken,
        loading: false,
        error: null,
      });
    } catch (error: any) {
      console.error('Login error:', error);

      setAuth(prev => ({ 
        ...prev,
        loading: false,
        isAuthenticated: false,
        error: error.response?.data?.message || 'Login failed. Please check your credentials.'
      }));

      throw error;
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    setAuth({
      ...initialAuthState,
      loading: false,
    });
  };

  // Signup function
  const signup = async (signupData: SignupRequest) => {
    try {
      setAuth(prev => ({ ...prev, loading: true, error: null }));

      await authService.signup(signupData);
      
      setAuth(prev => ({
        ...prev,
        loading: false,
      }));
    } catch (error: any) {
      console.error('Signup error:', error);

      setAuth(prev => ({
        ...prev,
        loading: false,
        error: error.response?.data?.message || 'Signup failed. Please try again.'
      }));

      throw error;
    }
  };

  // Clear error function
  const clearError = () => {
    setAuth(prev => ({ ...prev, error: null }));
  };

  // Create the context value
  const contextValue: AuthContextType = {
    auth,
    login,
    logout,
    signup,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 